import { useState } from 'react';
import { cn } from 'lib/cn';
import { Modal, Button, Input, Select, TextArea } from 'components/ui';

const MODES = [
  { key: 'remove', label: 'Remove', sign: -1 },
  { key: 'add', label: 'Add', sign: 1 },
];

/**
 * Adjust a single product's stock (add / remove + reason + notes).
 * Render with a `key` of the product id so state resets per product.
 */
export function AdjustStockModal({ product, reasons, saving, onSubmit, onClose }) {
  const [mode, setMode] = useState('remove');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState({});

  const reasonOptions = reasons.map((entry) => ({ value: entry.value, label: entry.displayValue }));
  const sign = MODES.find((m) => m.key === mode).sign;
  const qtyNum = Number(quantity) || 0;
  const delta = sign * qtyNum;
  const resulting = product?.stockQuantity != null ? product.stockQuantity + delta : null;
  const name = product?.vendorProductName || product?.name || 'Product';

  const handleSubmit = () => {
    const nextErrors = {};
    if (quantity === '' || qtyNum <= 0) nextErrors.quantity = 'Enter a quantity greater than 0.';
    if (!reason) nextErrors.reason = 'Select a reason.';
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    onSubmit({ quantityDelta: delta, reason, notes: notes.trim() || undefined });
  };

  return (
    <Modal
      isOpen={Boolean(product)}
      onClose={onClose}
      title="Adjust stock"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} isLoading={saving}>
            Record adjustment
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <p className="text-sm font-medium text-slate-900">{name}</p>
          <p className="text-xs text-slate-500">
            Current stock:{' '}
            <span className="font-medium text-slate-800">{product?.stockQuantity ?? '—'}</span>
          </p>
        </div>

        <div>
          <span className="mb-1.5 block text-sm font-medium text-slate-700">Adjustment</span>
          <div className="inline-flex rounded-lg border border-surface-300 bg-white p-1">
            {MODES.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => setMode(option.key)}
                className={cn(
                  'rounded-md px-5 py-1.5 text-sm font-medium transition',
                  mode === option.key
                    ? option.sign > 0
                      ? 'bg-brand-600 text-white'
                      : 'bg-red-600 text-white'
                    : 'text-slate-600 hover:text-slate-900',
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <Input
          label="Quantity"
          required
          autoFocus
          type="number"
          min="0"
          step="any"
          value={quantity}
          onChange={(event) => {
            setQuantity(event.target.value);
            setErrors((prev) => (prev.quantity ? { ...prev, quantity: undefined } : prev));
          }}
          error={errors.quantity}
        />
        {quantity !== '' && qtyNum > 0 && product?.stockQuantity != null && (
          <p className="-mt-2 text-sm text-slate-500">
            New stock will be <span className="font-semibold text-slate-900">{resulting}</span> (
            {delta >= 0 ? '+' : ''}
            {delta}).
          </p>
        )}

        <Select
          label="Reason"
          required
          placeholder="Select a reason"
          options={reasonOptions}
          value={reason}
          onChange={(event) => {
            setReason(event.target.value);
            setErrors((prev) => (prev.reason ? { ...prev, reason: undefined } : prev));
          }}
          error={errors.reason}
        />
        <TextArea
          label="Notes"
          rows={2}
          placeholder="Optional"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </div>
    </Modal>
  );
}

export default AdjustStockModal;
